import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { brutoCapex, netoDesdeBrutoCapex } from "../../utils/brutoCapex";
import {
  getPagosACuentaParaAnticipo,
  getVinculadoNetoPorCosto,
  vincularAnticipoCosto,
} from "../../utils/costosProyecto";
import { getLocale } from "../../utils/getLocale";
import { useCurrency } from "../../utils/useCurrency";

// Sub-modal "Vincular pago ya hecho" (menú ⋮ de CarteleriaCostosModal, Fase 5 — 2026-10-04).
//
// Vincula (parte de) un pago a cuenta de proveedor YA REGISTRADO en Banco/Caja (categoria_pago=
// 'pago_a_cuenta', todavía sin factura) como anticipo de esta línea de costo. NO crea ni edita
// movimientos/pagos/el libro — solo escribe el subledger `costos_proyecto_vinculos` vía la RPC
// `vincular_anticipo_costo` (10.190). La RPC vuelve a validar TODO del lado de la base (proveedor,
// país, categoría, estado, que no esté ya aplicado, topes); este modal solo filtra la lista para que
// el usuario no tenga que adivinar cuáles pagos son candidatos.
const VincularAnticipoCostoModal = ({ show, costo, negocio, userId, onHide, onVinculado }) => {
  const [cargando, setCargando] = useState(true);
  const [pagos, setPagos] = useState([]);
  const [remanenteLinea, setRemanenteLinea] = useState(null);
  const [seleccion, setSeleccion] = useState(""); // movimientoCajaId (string)
  const [montoBruto, setMontoBruto] = useState("");
  const [guardando, setGuardando] = useState(false);

  const { formatAmount } = useCurrency();
  const pais = negocio.pais;

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [candidatos, vinculadoMap] = await Promise.all([
          getPagosACuentaParaAnticipo({ pais, proveedorId: costo.proveedor_id }),
          getVinculadoNetoPorCosto([costo.id]),
        ]);
        if (!vivo) return;
        setPagos(candidatos);
        setRemanenteLinea(Number(costo.monto) - (vinculadoMap[costo.id] || 0));
      } catch (e) {
        toast.error(`Error al cargar pagos a cuenta: ${e.message || e}`);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costo.id]);

  const pagoSeleccionado = useMemo(
    () => pagos.find((p) => String(p.movimientoCajaId) === seleccion) || null,
    [pagos, seleccion]
  );

  // Remanente de la línea medido en BRUTO (la caja es siempre bruto, regla 6) — tope de referencia
  // para el default del input; el tope REAL de validación lo hace la RPC en neto y en bruto por mov.
  const remanenteLineaBruto = useMemo(() => {
    if (remanenteLinea == null) return null;
    return brutoCapex(Math.max(0, remanenteLinea), { exento_iva: costo.exento_iva, moneda: costo.moneda, pais });
  }, [remanenteLinea, costo.exento_iva, costo.moneda, pais]);

  const handleSeleccionar = (value) => {
    setSeleccion(value);
    const p = pagos.find((x) => String(x.movimientoCajaId) === value);
    if (p && remanenteLineaBruto != null) {
      setMontoBruto(String(Math.max(0, Math.min(remanenteLineaBruto, p.disponibleBruto))));
    }
  };

  const montoNetoCalculado = useMemo(() => {
    if (!montoBruto) return 0;
    return netoDesdeBrutoCapex(Number(montoBruto), { exento_iva: costo.exento_iva, moneda: costo.moneda, pais });
  }, [montoBruto, costo.exento_iva, costo.moneda, pais]);

  const handleGuardar = async () => {
    if (!pagoSeleccionado) return toast.error("Seleccioná un pago a cuenta");
    const bruto = Number(montoBruto);
    if (!bruto || bruto <= 0) return toast.error("El monto a vincular debe ser mayor a 0");
    setGuardando(true);
    try {
      const resultado = await vincularAnticipoCosto({
        costoId: costo.id,
        movimientoCajaId: pagoSeleccionado.movimientoCajaId,
        montoNeto: montoNetoCalculado,
        montoBruto: bruto,
        usuarioId: Number.isFinite(Number(userId)) ? Number(userId) : null,
      });
      toast.success("Pago vinculado como anticipo");
      onVinculado?.(resultado);
    } catch (err) {
      // 10.177: el mensaje real de la RPC (candados de proveedor/país/categoría/topes), tal cual.
      toast.error("Error al vincular el anticipo: " + (err.message || err));
    } finally {
      setGuardando(false);
    }
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "s/fecha";
    return new Date(`${fechaStr}T12:00:00`).toLocaleDateString(getLocale(pais), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Vincular pago ya hecho — {costo?.concepto}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cargando ? (
          <div style={{ textAlign: "center", padding: 20 }}>Cargando…</div>
        ) : (
          <>
            <div style={{ fontSize: "0.8rem", color: "#6c757d", marginBottom: 12 }}>
              Remanente de la línea: <strong>{formatAmount(Math.max(0, remanenteLinea || 0), costo.moneda)}</strong>{" "}
              (neto). Solo se listan pagos a cuenta de este proveedor que todavía NO están aplicados a ninguna factura.
            </div>

            {pagos.length === 0 ? (
              <div style={{ color: "#999", textAlign: "center", padding: 20 }}>
                No hay pagos a cuenta disponibles de este proveedor.
              </div>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Pago a cuenta</Form.Label>
                <Form.Select value={seleccion} onChange={(e) => handleSeleccionar(e.target.value)}>
                  <option value="">Seleccioná un pago…</option>
                  {pagos.map((p) => (
                    <option key={p.movimientoCajaId} value={String(p.movimientoCajaId)}>
                      {formatFecha(p.fecha)} — disponible {formatAmount(p.disponibleBruto, p.moneda)} (bruto)
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {pagoSeleccionado && (
              <Form.Group className="mb-3">
                <Form.Label>Monto a vincular (bruto — lo que salió por caja)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={montoBruto}
                  onChange={(e) => setMontoBruto(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Equivalente neto: {formatAmount(montoNetoCalculado, costo.moneda)} (cubre el remanente de la línea,
                  10.195/10.246).
                </Form.Text>
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleGuardar} disabled={guardando || !pagoSeleccionado}>
          {guardando ? "Vinculando…" : "Vincular"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VincularAnticipoCostoModal;
